import { useMemo, useState } from 'react';
import Markdown from 'shared/components/Markdown';
import styled from 'styled-components';

import Button, { ButtonType } from 'inquiry/components/Button';
import Select from 'inquiry/components/Select';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { useStepGovernmentIdStyleContext } from 'inquiry/context/StepGovernmentIdStyleContext';
import { textForGovernmentId } from 'inquiry/lib/inquiry';
import { containerStyles, labelSSTStyles } from 'inquiry/mixins/form';
import { IdClassesByCountry } from 'inquiry/redux/thunk-actions/verification';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';
import { GovernmentIdClassLocalizations, StepGovernmentIdConfig } from 'lib/interfaces/StepsConfig';
import { InputSelectBaseStyles } from 'lib/interfaces/ui-components';
import { GovernmentIdEnabledIdClassByCountryEntry } from 'lib/interfaces/verificationConfig';

const Container = styled('div')`
  ${containerStyles}
  text-transform: capitalize;
`;

const OutlinedLabelContainer = styled('div')<{ styles: InputSelectBaseStyles | undefined }>`
  ${({ styles }) => labelSSTStyles(styles)}
`;

interface InquiryGovernmentIdAutoclassificationSelectViewProps {
  localizations: StepGovernmentIdConfig['localizations']['auto-classification-page'] | undefined;
  governmentIdClassLocalizations: GovernmentIdClassLocalizations | undefined;
  idClassesByCountries: IdClassesByCountry[];
  selectIdTypeAndContinue: (
    countryCode: CountryCode | undefined,
    idClass: GovernmentIdClass | undefined,
  ) => void;
  defaultCountry: string;
}

export default function InquiryGovernmentIdAutoclassificationSelectView({
  localizations,
  governmentIdClassLocalizations,
  idClassesByCountries,
  selectIdTypeAndContinue,
  defaultCountry,
}: InquiryGovernmentIdAutoclassificationSelectViewProps) {
  const {
    base,
    buttons: buttonStyles,
    inputSelect: inputSelectStyles,
  } = useStepGovernmentIdStyleContext();

  const [countryCode, setCountryCode] = useState<CountryCode | ''>(() => {
    if (
      (
        idClassesByCountries.map(
          (idClassesByCountry) => idClassesByCountry['country-code'],
        ) as string[]
      ).includes(defaultCountry)
    ) {
      return defaultCountry as CountryCode;
    }
    return '';
  });
  const [idClass, setIdClass] = useState<GovernmentIdClass | ''>('');

  const countryOptions = useMemo(() => {
    return idClassesByCountries.map((idClassForCountry: IdClassesByCountry) => {
      return {
        text: idClassForCountry['country-name'],
        value: idClassForCountry['country-code'],
      };
    });
  }, [idClassesByCountries]);

  const idClassOptions = useMemo(() => {
    const idClassesForCurrentCountry = idClassesByCountries.filter(
      (idClassForCountry: IdClassesByCountry) => {
        return idClassForCountry['country-code'] === countryCode;
      },
    );
    if (idClassesForCurrentCountry.length === 0) {
      return [];
    }
    return idClassesForCurrentCountry[0]['id-configs'].map(
      (idConfig: GovernmentIdEnabledIdClassByCountryEntry) => {
        return {
          text:
            textForGovernmentId(
              governmentIdClassLocalizations,
              countryCode === '' ? undefined : countryCode,
              idConfig.class,
            ) ?? idConfig.class,
          value: idConfig.class,
        };
      },
    );
  }, [idClassesByCountries, governmentIdClassLocalizations, countryCode]);

  const handleContinue = () => {
    if (countryCode !== '' && idClass !== '') {
      selectIdTypeAndContinue(countryCode, idClass);
    }
  };

  const onCountrySelect = (value: string) => {
    if (value !== countryCode) {
      setIdClass('');
    }
    setCountryCode(value as CountryCode);
  };

  const onIdClassSelect = (value: string) => {
    setIdClass(value as GovernmentIdClass);
  };

  const title = localizations?.['manual-classification-title'] ?? 'Select your country and ID type';
  const countryInputTitle = localizations?.['country-input-title'] ?? 'Country';
  const idClassInputTitle = localizations?.['id-class-input-title'] ?? 'Accepted IDs';
  const continueButtonText =
    localizations?.['manual-classification-continue-button-text'] ?? 'Continue';

  return (
    <>
      <ThemedTitle id='autoclassification-select__title' styles={base?.titleStyle}>
        <Markdown>{title}</Markdown>
      </ThemedTitle>
      <SelectWithLabel
        styles={inputSelectStyles?.base}
        id='autoclassification-select__country'
        label={countryInputTitle}
        value={countryCode}
        options={countryOptions}
        handleChange={onCountrySelect}
      />
      {countryCode && (
        <SelectWithLabel
          styles={inputSelectStyles?.base}
          id='autoclassification-select__id_class'
          label={idClassInputTitle}
          value={idClass}
          options={idClassOptions}
          handleChange={onIdClassSelect}
        />
      )}
      <div>
        <Button
          type='button'
          id='autoclassification-select__continue_btn'
          buttonType={ButtonType.Primary}
          styles={buttonStyles?.[ButtonType.Primary]}
          disabled={countryCode === '' || idClass === ''}
          onClick={handleContinue}
        >
          {continueButtonText}
        </Button>
      </div>
    </>
  );
}

interface SelectWithLabelProps {
  styles: InputSelectBaseStyles | undefined;
  id: string;
  label: string;
  value: string | undefined;
  options: { text: string | undefined; value: string | undefined }[];
  handleChange: (value: string) => void;
}

function SelectWithLabel({
  styles,
  id,
  label,
  value,
  options,
  handleChange,
}: SelectWithLabelProps) {
  return (
    <OutlinedLabelContainer styles={styles}>
      <Markdown<'label'> wrapper='label' wrapperProps={{ htmlFor: id }}>
        {label}
      </Markdown>
      <Container>
        <Select
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          id={id}
          styles={styles}
        >
          <option value=''></option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </Select>
      </Container>
    </OutlinedLabelContainer>
  );
}
